/**
 * Renders the backend Pricing Engine's Price Quote verbatim — every number
 * shown here comes directly from `PriceQuote` (never computed in this
 * component), displayed with exact (not rounded-to-whole-rupee) precision,
 * since this is a binding payable amount. Field shapes follow
 * `05_API_SPECIFICATION.md`'s Calculate Price response exactly: `PriceQuote`
 * is flat (`totalPaise`/`currency` at the top level), and the base price is
 * the `BASE_PRICE` line inside `breakdown`, not a separate top-level field.
 *
 * InviteCloud has no cart/quantity concept (one Collection per Purchase,
 * `00_PAYMENT_SYSTEM_ARCHITECTURE.md §9`) — "multiple line items" in this
 * domain means multiple *discount* lines, not multiple products. Per
 * `04_PRICING_ENGINE.md §5`'s pipeline, the breakdown has at most one
 * `PROMOTION` slot and one `COUPON` slot, never an open-ended array of
 * either — this component renders each of those two fixed slots when
 * `applied: true`, it does not render an arbitrary list.
 */
import { Icons } from '@/src/modules/components/Icons';
import { formatPrice } from '@/src/infrastructure/lib/currency';
import { PRICING_CALCULATION_FAILED_MESSAGE, PRODUCT_UNAVAILABLE_MESSAGE } from '../copy';
import type { PriceQuote, BasePriceLine, CouponLine, PromotionLine, TaxLine } from '../types';

export interface PriceBreakdownProps {
  quote: PriceQuote | null;
  /** True only on the very first calculation — no quote to show yet. */
  loading: boolean;
  /** True while a later calculation is in flight and a quote already exists — the old quote stays visible, clearly marked as updating. */
  recalculating?: boolean;
  /** Set when a calculation attempt failed outright; `quote`, if present, is still the last-known-good price. */
  error?: string | null;
  productUnavailable?: boolean;
  onRetry?: () => void;
}

function findBasePriceLine(breakdown: PriceQuote['breakdown']): BasePriceLine | undefined {
  return breakdown.find((line): line is BasePriceLine => line.type === 'BASE_PRICE');
}

function findPromotionLine(breakdown: PriceQuote['breakdown']): PromotionLine | undefined {
  return breakdown.find((line): line is PromotionLine => line.type === 'PROMOTION');
}

function findCouponLine(breakdown: PriceQuote['breakdown']): CouponLine | undefined {
  return breakdown.find((line): line is CouponLine => line.type === 'COUPON');
}

function findTaxLine(breakdown: PriceQuote['breakdown']): TaxLine | undefined {
  return breakdown.find((line): line is TaxLine => line.type === 'TAX');
}

/**
 * A discount-shaped line (`PROMOTION`/`COUPON`) is styled green only when
 * its own `amountPaise` is actually negative — deriving the color from the
 * backend's sign, not assuming "these two line types are always a discount"
 * (a positive amount here would be a surcharge, e.g. a regional/partner
 * uplift, and must not be presented as good news — caught in code review).
 */
function lineAmountClassName(amountPaise: number): string {
  return amountPaise < 0 ? 'font-medium text-status-published' : 'font-medium text-on-surface';
}

export function PriceBreakdown({
  quote,
  loading,
  recalculating,
  error,
  productUnavailable,
  onRetry,
}: PriceBreakdownProps) {
  if (loading) {
    return (
      <section
        aria-label="Price summary"
        aria-busy="true"
        className="rounded-[1.75rem] border border-outline-variant/30 bg-surface-lowest p-6"
      >
        <p className="text-sm font-medium text-on-surface-variant">Calculating price…</p>
        <div className="mt-4 space-y-3">
          <div className="h-4 w-full animate-pulse rounded-lg bg-surface-low" />
          <div className="h-4 w-5/6 animate-pulse rounded-lg bg-surface-low" />
          <div className="h-6 w-2/3 animate-pulse rounded-lg bg-surface-low" />
        </div>
      </section>
    );
  }

  if (productUnavailable) {
    return (
      <div
        role="alert"
        className="flex items-center gap-3 rounded-2xl bg-status-failed/10 border border-status-failed/30 px-6 py-4 text-status-failed font-medium text-sm"
      >
        <Icons.Warning size={18} className="flex-shrink-0" />
        {PRODUCT_UNAVAILABLE_MESSAGE}
      </div>
    );
  }

  // No quote at all yet and the very first calculation failed — nothing
  // authoritative to show, only the error and a way to retry.
  if (!quote) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 rounded-2xl bg-status-failed/10 border border-status-failed/30 px-6 py-5 text-center"
      >
        <p className="text-status-failed font-medium text-sm">
          {error ?? PRICING_CALCULATION_FAILED_MESSAGE}
        </p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="text-sm font-semibold text-primary hover:underline"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  const basePriceLine = findBasePriceLine(quote.breakdown);
  const promotionLine = findPromotionLine(quote.breakdown);
  const couponLine = findCouponLine(quote.breakdown);
  const taxLine = findTaxLine(quote.breakdown);

  return (
    <section
      aria-label="Price summary"
      aria-busy={recalculating ? 'true' : 'false'}
      className="rounded-[1.75rem] border border-outline-variant/30 bg-surface-lowest p-6"
    >
      <h2 className="text-base font-bold text-on-surface">Price Summary</h2>

      {recalculating && (
        <p role="status" className="mt-1 text-xs font-medium text-primary">
          Updating price…
        </p>
      )}

      <dl className={recalculating ? 'mt-4 space-y-3 opacity-60 transition-opacity' : 'mt-4 space-y-3 transition-opacity'}>
        {basePriceLine && (
          <div className="flex items-baseline justify-between text-sm">
            <dt className="text-on-surface-variant">Product price</dt>
            <dd className="font-medium text-on-surface">
              {formatPrice(basePriceLine.amountPaise, quote.currency, { exact: true })}
            </dd>
          </div>
        )}

        {promotionLine?.applied && (
          <div className="flex items-baseline justify-between text-sm">
            <dt className="text-on-surface-variant">{promotionLine.label}</dt>
            {/* The backend's own sign is rendered as-is (no Math.abs + hardcoded
                "−") — a discount line's sign is a pricing decision, not
                something this component should assume or override. */}
            <dd className={lineAmountClassName(promotionLine.amountPaise)}>
              {formatPrice(promotionLine.amountPaise, quote.currency, { exact: true })}
            </dd>
          </div>
        )}

        {couponLine?.applied && (
          <div className="flex items-baseline justify-between text-sm">
            <dt className="text-on-surface-variant">Coupon {couponLine.label}</dt>
            <dd className={lineAmountClassName(couponLine.amountPaise)}>
              {formatPrice(couponLine.amountPaise, quote.currency, { exact: true })}
            </dd>
          </div>
        )}

        {taxLine && (
          <div className="flex items-baseline justify-between text-sm">
            <dt className="text-on-surface-variant">{taxLine.label}</dt>
            <dd className="font-medium text-on-surface">
              {formatPrice(taxLine.amountPaise, quote.currency, { exact: true })}
            </dd>
          </div>
        )}
      </dl>

      <div className="mt-4 flex items-baseline justify-between border-t border-outline-variant/20 pt-4">
        <span className="text-sm font-bold text-on-surface">Total</span>
        <span
          aria-live="polite"
          aria-label={`Total payable: ${formatPrice(quote.totalPaise, quote.currency, { exact: true })}`}
          className="text-2xl font-bold text-on-surface"
        >
          {formatPrice(quote.totalPaise, quote.currency, { exact: true })}
        </span>
      </div>

      {/* A recalculation failed but we still have the last-known-good price above — show both. */}
      {error && (
        <div
          role="alert"
          className="mt-4 flex items-center justify-between gap-3 rounded-xl bg-status-failed/10 border border-status-failed/30 px-4 py-3 text-sm"
        >
          <span className="text-status-failed font-medium">{error}</span>
          {onRetry && (
            <button type="button" onClick={onRetry} className="font-semibold text-primary hover:underline shrink-0">
              Retry
            </button>
          )}
        </div>
      )}
    </section>
  );
}
